import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Wrench, CheckCircle, Circle, Plus } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export default function WarrantyPanel({ project, reload }) {
  const { hasRole } = useAuth();
  const canManage = hasRole("pm", "inginer", "admin");
  const [visit, setVisit] = useState({ date: "", type: "Revizie planificată", notes: "" });

  const start = project.warranty_start;
  let endDate = null, remaining = null;
  if (start) {
    const s = new Date(start);
    const end = new Date(s);
    end.setMonth(end.getMonth() + (project.warranty_months || 24));
    endDate = end.toISOString();
    remaining = Math.max(0, Math.round((end - new Date()) / (1000 * 60 * 60 * 24 * 30)));
  }

  const activate = async () => {
    try { await api.post(`/projects/${project.id}/warranty/start`, { months: project.warranty_months || 24 }); toast.success("Garanție activată"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addVisit = async () => {
    if (!visit.date) return toast.error("Alege data reviziei");
    try { await api.post(`/projects/${project.id}/maintenance`, visit); toast.success("Revizie programată"); setVisit({ date: "", type: "Revizie planificată", notes: "" }); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (vid) => { await api.post(`/projects/${project.id}/maintenance/${vid}/toggle`); reload(); };

  const visits = project.maintenance_visits || [];

  return (
    <div className="space-y-4" data-testid="warranty-panel">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck size={20} weight="fill" className="text-emerald-400" />
          <h3 className="font-head text-lg font-bold">Garanție</h3>
        </div>
        {start ? (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
            <div><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Început</div><div className="mt-1 font-semibold">{fmtDate(start)}</div></div>
            <div><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Durată</div><div className="mt-1 font-semibold">{project.warranty_months} luni</div></div>
            <div><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Expiră</div><div className="mt-1 font-semibold">{fmtDate(endDate)}</div></div>
            <div><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Rămase</div><div className={cn("mt-1 font-head text-xl font-bold", remaining <= 3 ? "text-amber-400" : "text-emerald-400")}>{remaining} luni</div></div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Garanția nu este activată. Se activează după recepția finală / predare.</p>
            {canManage && <Button size="sm" onClick={activate} data-testid="activate-warranty-btn"><ShieldCheck size={15} className="mr-1" /> Activează garanția</Button>}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <Wrench size={18} className="text-primary" />
          <h3 className="font-head text-lg font-bold">Mentenanță & revizii</h3>
        </div>
        {canManage && (
          <div className="mb-4 flex flex-wrap gap-2">
            <Input type="date" value={visit.date} onChange={(e) => setVisit({ ...visit, date: e.target.value })} className="w-44" data-testid="visit-date" />
            <Select value={visit.type} onValueChange={(v) => setVisit({ ...visit, type: v })}>
              <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Revizie planificată", "Revizie în garanție", "Intervenție", "Inspecție"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Note" value={visit.notes} onChange={(e) => setVisit({ ...visit, notes: e.target.value })} className="flex-1 min-w-[140px]" />
            <Button onClick={addVisit} data-testid="add-visit-btn"><Plus size={15} /></Button>
          </div>
        )}
        <div className="space-y-2">
          {visits.map((v) => (
            <div key={v.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`visit-${v.id}`}>
              <button onClick={() => toggle(v.id)} className="text-muted-foreground hover:text-emerald-400">
                {v.done ? <CheckCircle size={18} weight="fill" className="text-emerald-400" /> : <Circle size={18} />}
              </button>
              <div className="flex-1">
                <span className={cn("text-sm font-medium", v.done && "line-through text-muted-foreground")}>{v.type}</span>
                {v.notes && <span className="ml-2 text-xs text-muted-foreground">{v.notes}</span>}
              </div>
              <span className="text-xs text-muted-foreground">{fmtDate(v.date)}</span>
            </div>
          ))}
          {visits.length === 0 && <p className="text-sm text-muted-foreground">Nicio revizie programată.</p>}
        </div>
      </div>
    </div>
  );
}
